import type { Co2Emission, EmissionCostLocationRef } from './co2Cost';
import { euEtsCountriesSet } from '../../shipment/EU_ETS_countries';
import {
  METRIC_TONNE_IN_GRAMS,
  calculateCh4Emissions,
  calculateN2oEmissions,
} from './ghg';

export const DEFAULT_CARBON_FACTOR = 3.206;

/**
 * EU ETS has a phase-in period during which only a part of total emissions
 * are considered
 *
 * Before 2024: 0%
 * 2024 - 40%
 * 2025 - 70%
 * 2026 onwards - 100%
 */
export function getEtsTransitionFactor(year: number): number {
  if (year < 2024) return 0;
  if (year === 2024) return 0.4;
  if (year === 2025) return 0.7;
  return 1.0;
}

export function getShipmentEtsFactor(
  shipmentCountries: string[],
  vesselGrossTonnage: number | null,
) {
  if (!vesselGrossTonnage) return 0;
  const isGTOver5000 = vesselGrossTonnage >= 5000;
  if (!isGTOver5000) return 0;

  const euCountries = shipmentCountries.filter((c) => euEtsCountriesSet.has(c));
  if (euCountries.length === shipmentCountries.length) return 1;
  if (euCountries.length > 0) return 0.5;
  return 0;
}

/** First year when CH4 and N2O are included in EU ETS calculations */
export const EU_ETS_GHG_INCLUSION_START_YEAR = 2026;

export interface EuEtsCo2Emission extends Co2Emission {
  locations: EmissionCostLocationRef[];
}

/**
 * Calculate CH4 emissions in CO2 equivalent tonnes
 */
export function calculateCh4Co2eqTonnes(
  consumedBunkerTonnes: number,
  ch4Factor: number | null,
): number | null {
  if (ch4Factor === null) return null;
  return (
    calculateCh4Emissions(consumedBunkerTonnes, ch4Factor) /
    METRIC_TONNE_IN_GRAMS
  );
}

/**
 * Calculate N2O emissions in CO2 equivalent tonnes
 */
export function calculateN2oCo2eqTonnes(
  consumedBunkerTonnes: number,
  n2oFactor: number | null,
): number | null {
  if (n2oFactor === null) return null;
  return (
    calculateN2oEmissions(consumedBunkerTonnes, n2oFactor) /
    METRIC_TONNE_IN_GRAMS
  );
}

/**
 * Check if CH4 and N2O should be included in EU ETS calculation (from 2026)
 */
export function shouldIncludeCh4AndN2o(startYear: number): boolean {
  return startYear >= EU_ETS_GHG_INCLUSION_START_YEAR;
}

export interface EuEtsEmissionTotals {
  totalCo2Emissions: number;
  totalCh4Co2eqEmissions: number;
  totalN2oCo2eqEmissions: number;
  totalCo2eqEmissions: number;
}

/**
 * Calculate total emissions for EU ETS cost calculation
 */
export function calculateEuEtsEmissionTotals(
  co2Emissions: EuEtsCo2Emission[],
  startYear: number,
): EuEtsEmissionTotals {
  const totalCo2Emissions = co2Emissions.reduce(
    (sum, { emittedCo2Tonnes }) => sum + emittedCo2Tonnes,
    0,
  );

  const includeCh4AndN2o = shouldIncludeCh4AndN2o(startYear);
  let totalCh4Co2eqEmissions = 0;
  let totalN2oCo2eqEmissions = 0;

  if (includeCh4AndN2o) {
    totalCh4Co2eqEmissions = co2Emissions.reduce(
      (sum, { emittedCh4Co2eqTonnes }) => sum + (emittedCh4Co2eqTonnes ?? 0),
      0,
    );
    totalN2oCo2eqEmissions = co2Emissions.reduce(
      (sum, { emittedN2oCo2eqTonnes }) => sum + (emittedN2oCo2eqTonnes ?? 0),
      0,
    );
  }

  const totalCo2eqEmissions =
    totalCo2Emissions + totalCh4Co2eqEmissions + totalN2oCo2eqEmissions;

  return {
    totalCo2Emissions,
    totalCh4Co2eqEmissions,
    totalN2oCo2eqEmissions,
    totalCo2eqEmissions,
  };
}

export interface EuEtsCostCalculationResult {
  /** The calculated EU ETS cost amount */
  amount: number;
  /** EU ETS transition factor for the year */
  transitionFactor: number;
  /** Factor based on whether shipment is intra-EU or extra-EU */
  etsFactor: number;
  /** Total CO2 emissions in tonnes */
  totalCo2Tonnes: number;
  /** Total CH4 emissions in CO2eq tonnes (null if not applicable) */
  totalCh4Co2eqTonnes: number | null;
  /** Total N2O emissions in CO2eq tonnes (null if not applicable) */
  totalN2oCo2eqTonnes: number | null;
}

/**
 * Calculate EU ETS cost for a set of emissions
 * Returns null if the cost should not be calculated (e.g., zero emissions, no ETS factor)
 */
export function calculateEuEtsCost(
  co2Emissions: EuEtsCo2Emission[],
  startYear: number,
  carbonPricePerTonne: number,
  vesselGrossTonnage: number,
): EuEtsCostCalculationResult | null {
  const locations = co2Emissions.at(0)?.locations ?? [];
  if (locations.length === 0) {
    console.error('No locations found for emission', co2Emissions.at(0));
    return null;
  }

  const emissionTotals = calculateEuEtsEmissionTotals(co2Emissions, startYear);

  // Skip if there are no emissions to calculate costs for
  if (emissionTotals.totalCo2eqEmissions <= 0) return null;

  // Calculate EU ETS transition factor for this year
  const transitionFactor = getEtsTransitionFactor(startYear);

  // Skip if no transition factor applicable yet
  if (transitionFactor <= 0) return null;

  // Calculate EU ETS factor based on countries and vessel
  const etsFactor = getShipmentEtsFactor(
    locations.map((loc) => loc.country),
    vesselGrossTonnage,
  );

  // Skip if no ETS factor applicable
  if (etsFactor <= 0) return null;

  // Calculate EU ETS cost
  const amount =
    emissionTotals.totalCo2eqEmissions *
    carbonPricePerTonne *
    transitionFactor *
    etsFactor;

  const includeCh4AndN2o = shouldIncludeCh4AndN2o(startYear);

  return {
    amount,
    transitionFactor,
    etsFactor,
    totalCo2Tonnes: emissionTotals.totalCo2Emissions,
    totalCh4Co2eqTonnes: includeCh4AndN2o
      ? emissionTotals.totalCh4Co2eqEmissions
      : null,
    totalN2oCo2eqTonnes: includeCh4AndN2o
      ? emissionTotals.totalN2oCo2eqEmissions
      : null,
  };
}
